import { Box, Typography } from "@mui/material";
import { IPaginatedList } from "Interfaces/PaginatedList";
import {
  DataGrid,
  esES,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { TableToolBar } from "./TableToolBar";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getFormalDate } from "utils/formatDate";
import { useNavigate } from "react-router-dom";
import { Modal } from "components/Modal";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/store";
import styles from "scss/Table.module.scss";
import defaultImgActivity from "../../../assets/defaultImgNew.jpg";
import {
  ICompanyState,
  setPageIndex,
  setPageSize,
  setSort,
} from "redux/features/companySlice";
import { ICompany } from "Models/Company";
import { ISelectOption } from "Interfaces/Utils";
import { useIntl } from "react-intl";
import { LanguageContext } from "LanguageProvider";
import { getMuiLocaleText } from "utils/getMUILocaleText";

interface CompanyTableProps {
  paginatedQuery?: IPaginatedList<ICompany>;
  isLoading: boolean;
  deleteCompany: (companyId: string) => void;
  provinces: ISelectOption<string>[];
}

export const Table = ({
  paginatedQuery,
  isLoading,
  deleteCompany,
  provinces,
}: CompanyTableProps) => {
  const { pageSize, sort, pageIndex } = useAppSelector(
    (state: { companyState: ICompanyState }) => state.companyState
  );

  const dispatch = useDispatch();

  const { formatMessage } = useIntl();
  const languageContext = useContext(LanguageContext);
  const locale = languageContext?.locale;
  const navigate = useNavigate();
  const localeText = useMemo(() => getMuiLocaleText(locale ?? "es", formatMessage), [locale, formatMessage]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [companyIdToDelete, setCompanyIdToDelete] = useState<string | undefined>(
    undefined
  );

  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;

  const rows = paginatedQuery?.data ?? [];

  

  const handleSort = (newSortModel: GridSortModel) => {
    dispatch(setSort(newSortModel));
  };

  const handleChangePage = (newPage: number) => {
    dispatch(setPageIndex(newPage));
  };

  const handleChangeRowsPerPage = (newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
  };

  const onDeleteOpenModal = (companyId: string) => {
    setOpenDeleteModal(true);
    setCompanyIdToDelete(companyId);
  };

  const onCloseDeleteModal = () => {
    setCompanyIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onAcceptDeleteModal = () => {
    if (companyIdToDelete) {
      deleteCompany(companyIdToDelete);
    }
    setCompanyIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onEdit = useCallback(
    (companyId: string) => {
      navigate("/companies/" + companyId);
    },
    [navigate]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "image",
        headerName: "",
        width: 80,
        renderCell(params) {
          return (
            <img
              src={
                params.row.image
                  ? `${IMAGE_SERVER}/${params.row.image}`
                  : defaultImgActivity
              }
              alt="Imagen de la noticia"
              style={{
                maxWidth: "100%",
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          );
        },
        sortable: false,
      },
      {
        field: "name",
        headerName: formatMessage({ id: "company.name" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.name}
            </Typography>
          );
        },
      },
      {
        field: "addressType",
        headerName: formatMessage({ id: "company.addressType" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressType}
            </Typography>
          );
        },
      },
      {
        field: "addressName",
        headerName: formatMessage({ id: "company.addressName" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressName}
            </Typography>
          );
        },
      },
      {
        field: "addressNumber",
        headerName: formatMessage({ id: "company.addressNumber" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressNumber}
            </Typography>
          );
        },
      },
      {
        field: "addressDetails",
        headerName: formatMessage({ id: "company.addressDetails" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.addressDetails}
            </Typography>
          );
        },
      },
      {
        field: "postalCode",
        headerName: formatMessage({ id: "company.postalCode" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.postalCode}
            </Typography>
          );
        },
      },
      {
        field: "province",
        headerName: formatMessage({ id: "company.province" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            > 
              {provinces.find((province) => province.value === params
                .row.province)?.label}
            </Typography>
          );
        }
      },   
      {
        field: "limit",
        headerName: formatMessage({ id: "company.limit" }),
        flex: 1,
        align: "right",
        headerAlign: "right",
        renderCell(params) {
          const formattedAmount = new Intl.NumberFormat("es-ES", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true,
          }).format(params.row.limit);
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {formattedAmount}
            </Typography>
          );
        },
      },
      {
        field: "nif",
        headerName: formatMessage({ id: "company.nif" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.nif}
            </Typography>
          );
        },
      },
      {
        field: "locality",
        headerName: formatMessage({ id: "company.locality" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.locality}
            </Typography>
          );
        },
      },   
      {
        field: "action",
        width: 80,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(params.row.id)}
            label="Editar"
            placeholder={"Editar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => onDeleteOpenModal(params.row.id)}
            label="Borrar"
            placeholder={"Borrar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
        ],
      },
    ],
    [IMAGE_SERVER, onEdit, locale]
  );

  return (
    <Box className={styles.DataGridContainer}>
      <Modal.DeleteModal
        open={openDeleteModal}
        title={formatMessage({ id: "company.deleteModalMessage" })}
        onAcceptHandler={onAcceptDeleteModal}
        onCancelHandler={onCloseDeleteModal}
      />
      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        pageSize={pageSize}
        page={pageIndex}
        disableSelectionOnClick
        paginationMode="server"
        onPageChange={(newPage) => {
          handleChangePage(newPage);
        }}
        onPageSizeChange={(newPageSize) => handleChangeRowsPerPage(newPageSize)}
        sortModel={sort}
        onSortModelChange={(newSortModel) => handleSort(newSortModel)}
        hideFooter={rows.length === 0}
        autoHeight={rows.length !== 0}
        rowsPerPageOptions={[10, 20, 25]}
        rowCount={paginatedQuery?.total ?? 0}
        localeText={localeText}
        rowHeight={80}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#718096",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-virtualScrollerRenderZone": {
            padding: "0px 0px"
          },
        }}
        loading={isLoading}
      />
    </Box>
  );
};
